import type { MatchState, CelebrationState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, TeamIdx } from '../types';
import { rngInt } from '../math/rng';

// Goal celebration ceremony (D108). Pure — no I/O, no wall clock, no Math.random. Every draw comes
// from `state.rng` in the normative order (§7), and the result is stored in `state.celebration` so
// replay reproduces it exactly.
//
// The ceremony is DRIVEN, like D38's restart-taker teleport and D83's restart ceremony: the rules
// step writes velocity and facing directly rather than asking the AI for it, so it looks the same
// whether a player is a bot or a human holding the stick.

/** Eligible celebrants: on the pitch and not sent off. */
function availablePlayers(state: MatchState, team: TeamIdx): EntityId[] {
  return state.players.filter((p) => p.team === team && !p.sentOff).map((p) => p.id);
}

/**
 * Who plays the celebration clip.
 *
 * Normally just the scorer. On a genuine own goal nobody can be credited — the scoring team still
 * celebrates, so a seeded few of them are drawn (partial Fisher–Yates, so the draw count is fixed
 * and the order is stable for a given rng state).
 */
export function pickCelebrants(state: MatchState, team: TeamIdx, scorer: EntityId | null, cfg: SimConfig): EntityId[] {
  if (scorer !== null) return [scorer];
  const pool = availablePlayers(state, team);
  const want = Math.min(cfg.rules.celebration.OWN_GOAL_CELEBRANTS, pool.length);
  for (let i = 0; i < want; i++) {
    const j = i + rngInt(state, pool.length - i);
    const a = pool[i] as EntityId;
    pool[i] = pool[j] as EntityId;
    pool[j] = a;
  }
  return pool.slice(0, want);
}

/**
 * Build the celebration record for a goal. Call ONCE, at the goal.
 *
 * `scorer` is the credited scorer, or null for a genuine own goal (see detectGoal's D48 attribution:
 * a deflected shot still belongs to the shooter and is never an own goal).
 *
 * A normal goal consumes NO randomness — the §7 normative draw order is untouched, and only the
 * own-goal path (which has to choose who celebrates) advances `state.rng`.
 */
export function beginCelebration(state: MatchState, team: TeamIdx, scorer: EntityId | null, cfg: SimConfig): CelebrationState {
  const celebrants = pickCelebrants(state, team, scorer, cfg);
  // D108a: set the action HERE, once. The first cut re-set it every tick from applyCelebration, which
  // pinned actionTicks at 0 forever — so the action-table timer could never elapse and the player was
  // stuck in the celebration animation for the rest of the match.
  for (const id of celebrants) {
    const p = state.players[id];
    if (!p) continue;
    p.action = 'CELEBRATE';
    p.actionTicks = 0;
    p.charge = null;
  }
  return { scorer, team, celebrants };
}

/**
 * Per-tick ceremony while GOAL_CELEBRATION runs. No-op in every other phase.
 *
 * - celebrants stand still in CELEBRATE, facing the crowd side they scored towards
 * - the rest of the scoring team orbits the scorer (self-correcting onto the ring, so nobody has to
 *   carry an angle in state)
 * - the conceding team trudges back towards its own half, fanned out by entity id so they do not
 *   move as one block
 */
export function applyCelebration(state: MatchState, cfg: SimConfig): void {
  const c = state.celebration;
  if (state.phase !== 'GOAL_CELEBRATION' || c === null) return;
  const C = cfg.rules.celebration;
  const anchor = celebrationAnchor(state, c);
  const celebrants = new Set<EntityId>(c.celebrants);

  for (const p of state.players) {
    if (p.sentOff) continue;
    if (celebrants.has(p.id)) {
      // Held still for the whole ceremony. The ACTION is not touched here — beginCelebration sets it
      // once and the §5.4 action table retires it after DUR.CELEBRATE, exactly like every other
      // timed action. Re-setting it per tick is what made it permanent (D108a).
      p.vel = { x: 0, y: 0 };
      continue;
    }
    if (p.team === c.team) {
      // mob the scorer: tangential jog + a radial correction back onto the ring
      const dx = p.pos.x - anchor.x;
      const dy = p.pos.y - anchor.y;
      const d = Math.hypot(dx, dy) || 1;
      const ux = dx / d;
      const uy = dy / d;
      const radialErr = d - C.ORBIT_RADIUS;
      p.vel = {
        x: -uy * C.ORBIT_SPEED - ux * radialErr * C.ORBIT_PULL,
        y: ux * C.ORBIT_SPEED - uy * radialErr * C.ORBIT_PULL,
      };
      p.facing = Math.atan2(anchor.y - p.pos.y, anchor.x - p.pos.x); // eyes on the scorer
    } else {
      // heads down, back towards their own half; the id term keeps them from marching in lockstep
      const home = -state.attackDir[p.team];
      const spread = ((p.id % 5) - 2) * 0.25; // ±0.5 rad
      const dir = Math.atan2(0, home) + spread;
      p.vel = { x: Math.cos(dir) * C.MOPE_SPEED, y: Math.sin(dir) * C.MOPE_SPEED };
      p.facing = dir;
    }
  }
}

/** What the scoring team runs around: the scorer, else the celebrants' centre, else the ball. */
function celebrationAnchor(state: MatchState, c: CelebrationState): { x: number; y: number } {
  if (c.scorer !== null) {
    const s = state.players[c.scorer];
    if (s) return { x: s.pos.x, y: s.pos.y };
  }
  if (c.celebrants.length > 0) {
    let x = 0;
    let y = 0;
    let n = 0;
    for (const id of c.celebrants) {
      const p = state.players[id];
      if (!p) continue;
      x += p.pos.x;
      y += p.pos.y;
      n += 1;
    }
    if (n > 0) return { x: x / n, y: y / n };
  }
  return { x: state.ball.pos.x, y: state.ball.pos.y };
}
